import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { 
    PlusCircle, Search, Edit2, Trash2, ChevronRight, ChevronLeft, 
    ChevronsLeft, ChevronsRight, Filter, Download, Activity, Clock,
    BookOpen, Layers, Hash, Coins, CheckCircle2, XCircle
} from 'lucide-react';
import API_BASE_URL from '../Config';

const PlanCuentasList = () => {
    const [planCuentas, setPlanCuentas] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [goToPage, setGoToPage] = useState('1');

    useEffect(() => {
        fetchPlanCuentas();
    }, []);

    const fetchPlanCuentas = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/plan-cuentas`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('No se pudieron cargar los planes de cuenta');
            const data = await response.json();
            setPlanCuentas(Array.isArray(data) ? data : []);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEliminar = async (id) => {
        const confirm = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });

        if (confirm.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/plan-cuentas/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('No se pudo eliminar el plan de cuenta');
                Swal.fire('Eliminado', 'El plan de cuenta fue eliminado correctamente', 'success');
                fetchPlanCuentas();
            } catch (error) {
                Swal.fire('Error', error.message, 'error');
            }
        }
    };

    const filteredPlanCuentas = planCuentas.filter(p =>
        p.codigoIncoop?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nombreCuenta?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.tipoCuenta?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const totalItems = filteredPlanCuentas.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    const currentItems = filteredPlanCuentas.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
        setGoToPage('1');
    }, [searchTerm, rowsPerPage]);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) {
                handlePageChange(page);
            }
        }
    };

    const tipoCuentaLabel = (tipo) => {
        const tipos = { A: 'Activo', P: 'Pasivo', I: 'Ingreso', E: 'Egreso', O: 'Otro' };
        return tipos[tipo] ?? tipo;
    };

    return (
        <div className="bg-slate-50 min-h-screen">
            {/* Header & Controls */}
            <div className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-1">Plan de Cuentas</h1>
                        <p className="text-slate-500 font-medium text-sm">Parametrización del sistema contable</p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Buscar por código o nombre..."
                                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all text-xs font-bold shadow-sm"
                            onClick={() => navigate('/parametrizacion/plan-cuentas/nuevo')}
                        >
                            <PlusCircle className="w-4 h-4" />
                            Nueva Cuenta
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="py-24 flex flex-col items-center justify-center">
                        <div className="animate-spin h-8 w-8 border-[3px] border-blue-600 border-t-transparent rounded-full mb-4"></div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Cargando plan de cuentas...</p>
                    </div>
                ) : filteredPlanCuentas.length === 0 ? (
                    <div className="py-24 flex flex-col items-center justify-center text-slate-400">
                        <BookOpen className="w-12 h-12 opacity-20 mb-4" />
                        <h3 className="text-lg font-bold">No hay cuentas registradas</h3>
                        <p className="text-sm">Ajuste su búsqueda o añada una nueva cuenta.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-200 bg-white">
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-16 text-center">ID</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-32">Código</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider">Nombre de la Cuenta</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24">Tipo</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-20 text-center">Nivel</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24 text-center">Imputable</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24">Moneda</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-24">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentItems.map(p => (
                                    <tr key={p.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-4 py-3 text-center text-xs font-bold text-slate-400">
                                            {p.id}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-[10px] font-mono font-black text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                                {p.codigoIncoop}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                                                    {p.nombreCuenta}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${
                                                p.tipoCuenta === 'A' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                                p.tipoCuenta === 'P' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                                                p.tipoCuenta === 'I' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                                                'bg-rose-50 text-rose-600 border border-rose-100'
                                            }`}>
                                                {tipoCuentaLabel(p.tipoCuenta)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className="text-xs font-bold text-slate-500 bg-slate-50 w-6 h-6 inline-flex items-center justify-center rounded-full border border-slate-100">
                                                {p.nivel}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {p.esImputable ? (
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                                            ) : (
                                                <XCircle className="w-4 h-4 text-slate-300 mx-auto" />
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                                                <Coins className="w-3 h-3 text-slate-400" />
                                                {p.moneda?.nombre ?? '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    onClick={() => navigate(`/parametrizacion/plan-cuentas/${p.id}/editar`)}
                                                    title="Editar"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button
                                                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                    onClick={() => handleEliminar(p.id)}
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Paginación estilo Premium */}
                {!loading && filteredPlanCuentas.length > 0 && (
                    <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ir a</span>
                                <input 
                                    type="text" 
                                    className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                    value={goToPage}
                                    onChange={(e) => setGoToPage(e.target.value)}
                                    onKeyDown={handleGoToPage}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ver</span>
                                <select 
                                    className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    value={rowsPerPage}
                                    onChange={(e) => {
                                        setRowsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    {[10, 20, 50, 100].map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <div className="flex items-center mx-2 gap-1">
                                {[...Array(totalPages)].map((_, i) => {
                                    const p = i + 1;
                                    if (totalPages > 5) {
                                        if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                            if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                            return null;
                                        }
                                    }
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => handlePageChange(p)}
                                            className={`w-8 h-8 rounded text-xs font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>

                            <button 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            [{totalItems > 0 ? startIndex + 1 : 0} a {endIndex} de {totalItems}]
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlanCuentasList;